import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteForever } from '@mui/icons-material';
import {
  getCountFromServer,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { attendeesCollectionRef } from '../../lib/firebase';
import { bulkDeleteAttendeesForEvent, useActor } from '../../lib/audited-writes';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { AppEvent } from '../../lib/types';

const DeleteAttendeesDialog = ({
  event,
  onClose,
  onDone,
  onError,
}: {
  event: AppEvent | null;
  onClose: () => void;
  onDone: (deleted: number, eventName: string) => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const [confirmText, setConfirmText] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [countError, setCountError] = useState(false);

  // Reset on open / close and fetch the current attendee count for this
  // event so the user can sanity-check what they're about to delete.
  // getCountFromServer is cheap (a single aggregation read) compared to
  // pulling the whole collection. It includes is_deleted records too —
  // a wipe is a wipe.
  useEffect(() => {
    if (!event) {
      setConfirmText('');
      setCount(null);
      setCountError(false);
      return;
    }
    setConfirmText('');
    setCount(null);
    setCountError(false);
    let cancelled = false;
    getCountFromServer(
      query(attendeesCollectionRef, where('eventRef', '==', event.ref)),
    )
      .then((snap) => {
        if (cancelled) return;
        setCount(snap.data().count);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to count attendees for event', err);
        setCountError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [event]);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  // Match the event name exactly (case-sensitive, trimmed). GitHub-style
  // typed confirmation: the user must reproduce the target's name, which
  // both proves intent and confirms they're aimed at the right event.
  const expected = event?.name ?? '';
  const isValid =
    Boolean(event) && confirmText.trim() === expected && expected.length > 0;

  const handleConfirm = async () => {
    if (!event || !isValid) return;
    setIsWorking(true);
    try {
      const snap = await getDocs(
        query(attendeesCollectionRef, where('eventRef', '==', event.ref)),
      );
      const deleted = await bulkDeleteAttendeesForEvent(actor, event, snap.docs);
      onDone(deleted, event.name);
      onClose();
    } catch (error) {
      console.error('Error deleting attendees', error);
      onError('Failed to delete attendees');
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog open={Boolean(event)} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ color: 'error.main' }}>
        Delete all attendees
      </DialogTitle>
      <DialogContent>
        {event && (
          <Stack spacing={2}>
            <Alert severity='error'>
              This permanently deletes every attendee record for{' '}
              <strong>{event.name}</strong>. Check-in history for this event
              will lose its names (the date docs keep the refs, but the
              attendees they point to will be gone). This cannot be undone.
            </Alert>

            <DialogContentText>
              {count === null && !countError && (
                <Box
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
                >
                  <CircularProgress size={14} />
                  Counting attendees…
                </Box>
              )}
              {countError && (
                <Typography color='warning.main' component='span'>
                  Could not load attendee count. Proceed at your own risk.
                </Typography>
              )}
              {count !== null && (
                <>
                  <strong>{count}</strong> attendee{count === 1 ? '' : 's'}{' '}
                  will be permanently deleted.
                </>
              )}
            </DialogContentText>

            <DialogContentText>
              To confirm, type the event name exactly:
            </DialogContentText>
            <Box>
              <Chip
                label={event.name}
                size='small'
                variant='outlined'
                sx={{ mb: 1, fontFamily: 'monospace' }}
              />
            </Box>
            <TextField
              autoFocus
              fullWidth
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={event.name}
              disabled={isWorking}
              inputProps={{ 'aria-label': 'Confirm event name' }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color='error'
          variant='contained'
          disabled={!isValid || isWorking || count === 0}
          startIcon={
            isWorking ? <CircularProgress size={16} /> : <DeleteForever />
          }
        >
          {isWorking ? 'Deleting…' : 'Delete all attendees'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DangerZone = () => {
  const { events } = useFirebaseContext();
  const [deleting, setDeleting] = useState<AppEvent | null>(null);
  const toast = useToast();

  // Same sort order as the main events list: active first, then non-archived,
  // then by name. Keeps the mental mapping consistent across the admin page.
  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => {
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        const aArch = Boolean(a.isArchived);
        const bArch = Boolean(b.isArchived);
        if (aArch !== bArch) return aArch ? 1 : -1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      }),
    [events],
  );

  return (
    <>
      <Alert severity='warning' sx={{ mb: 2 }}>
        Actions here are <strong>permanent and cannot be undone</strong>. They
        only affect the event you select.
      </Alert>

      {sortedEvents.length === 0 ? (
        <Typography color='text.secondary' textAlign='center' sx={{ py: 2 }}>
          No events yet.
        </Typography>
      ) : (
        <List dense disablePadding>
          {sortedEvents.map((event) => (
            <ListItem
              key={event.ref}
              divider
              sx={{
                py: 0.5,
                px: 1,
                gap: 1,
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500 }}
                    noWrap
                  >
                    {event.name}
                  </Typography>
                  {event.isActive && (
                    <Chip
                      size='small'
                      color='success'
                      variant='outlined'
                      label='Active'
                    />
                  )}
                  {event.isArchived && (
                    <Chip
                      size='small'
                      color='default'
                      variant='outlined'
                      label='Archived'
                    />
                  )}
                </Stack>
              </Box>
              <Button
                size='small'
                color='error'
                variant='outlined'
                startIcon={<DeleteForever />}
                onClick={() => setDeleting(event)}
              >
                Delete all attendees
              </Button>
            </ListItem>
          ))}
        </List>
      )}

      <DeleteAttendeesDialog
        event={deleting}
        onClose={() => setDeleting(null)}
        onDone={(deleted, name) =>
          toast.success(
            `Deleted ${deleted} attendee${deleted === 1 ? '' : 's'} from "${name}"`,
          )
        }
        onError={(msg) => toast.error(msg)}
      />
    </>
  );
};

export default DangerZone;
